/**
 * Compliance Reports (Phase 2)
 *
 * Three report kinds:
 *   • fund_wise         — Fund-wise consolidated (pick fund + optional date range)
 *   • monthly           — All funds, one month (YYYY-MM)
 *   • annual_calendar   — All funds, one calendar year
 *
 * Preview shows summary tiles + up to 500 rows; downloads produce XLSX / PDF.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, FileText, Loader, BarChart3, ChevronLeft } from 'lucide-react';
import { complianceAPI, fundAPI } from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';

const STATUS_BADGE = {
  Complied: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Not Complied': 'bg-red-100 text-red-800 border-red-200',
  'On Track': 'bg-blue-100 text-blue-800 border-blue-200',
  'Not Applicable': 'bg-gray-200 text-gray-600 border-gray-300',
};

const KIND_LABELS = {
  fund_wise: 'Fund-wise Consolidated',
  monthly: 'Monthly Due-date Report',
  annual_calendar: 'Annual Compliance Calendar',
};

const today = new Date();
const yyyymm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

const ComplianceReports = () => {
  const fmtDate = useDateFormat();
  const [funds, setFunds] = useState([]);
  const [kind, setKind] = useState('fund_wise');
  const [fundId, setFundId] = useState('');
  const [month, setMonth] = useState(yyyymm);
  const [year, setYear] = useState(today.getFullYear());
  const [yearType, setYearType] = useState('calendar');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fundAPI.getAll().then(r => {
      setFunds(r.data || []);
      // fund_wise requires a fund; monthly/annual default to All Funds
      if (r.data?.length) setFundId(r.data[0].id);
    });
  }, []);  // eslint-disable-line

  const params = useMemo(() => {
    const p = { kind };
    if (fundId) p.fund_id = fundId;
    if (kind === 'monthly') p.month = month;
    if (kind === 'annual_calendar') { p.year = year; p.year_type = yearType; }
    if (kind === 'fund_wise') {
      if (fromDate) p.from_date = fromDate;
      if (toDate) p.to_date = toDate;
    }
    return p;
  }, [kind, fundId, month, year, yearType, fromDate, toDate]);

  const canRun = kind !== 'fund_wise' || !!fundId;

  const runPreview = async () => {
    if (!canRun) return;
    setLoading(true);
    setError(null);
    try {
      const res = await complianceAPI.previewReport(params);
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load report');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const download = async (fmt) => {
    if (!canRun) return;
    setDownloading(fmt);
    try {
      const res = await complianceAPI.downloadReport({ ...params, fmt });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      const ext = fmt === 'xlsx' ? 'xlsx' : 'pdf';
      a.href = url;
      a.download = `${preview?.title || 'compliance_report'}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="p-6 max-w-[110rem] mx-auto" data-testid="compliance-reports-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <Link to="/compliance" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-1">
            <ChevronLeft size={14} /> Back to Tracker
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 inline-flex items-center gap-2">
            <BarChart3 size={26} className="text-blue-600" /> Compliance Reports
          </h1>
          <p className="text-gray-600 mt-1">Generate fund-wise, monthly or annual compliance reports and export them as Excel or PDF.</p>
        </div>
      </div>

      {/* Report picker */}
      <div className="bg-white border rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Report Type</label>
          <select value={kind} onChange={e => {
                    const k = e.target.value;
                    setKind(k);
                    setPreview(null);
                    // fund_wise needs a fund; monthly/annual default to All Funds
                    if (k === 'fund_wise' && !fundId && funds.length) setFundId(funds[0].id);
                    if (k !== 'fund_wise') setFundId('');
                  }}
                  className="border rounded px-3 py-2 text-sm" data-testid="report-kind">
            {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Fund</label>
          <select value={fundId} onChange={e => setFundId(e.target.value)}
                  className="border rounded px-3 py-2 text-sm min-w-[220px]" data-testid="report-fund">
            {kind === 'fund_wise' && <option value="">Choose fund…</option>}
            {kind !== 'fund_wise' && <option value="">All Funds</option>}
            {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>

        {kind === 'fund_wise' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Due from (optional)</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                     className="border rounded px-3 py-2 text-sm" data-testid="report-from-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Due to (optional)</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                     className="border rounded px-3 py-2 text-sm" data-testid="report-to-date" />
            </div>
          </>
        )}

        {kind === 'monthly' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                   className="border rounded px-3 py-2 text-sm" data-testid="report-month" />
          </div>
        )}
        {kind === 'annual_calendar' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Year Type</label>
              <select value={yearType} onChange={e => setYearType(e.target.value)}
                      className="border rounded px-3 py-2 text-sm" data-testid="report-year-type">
                <option value="calendar">Calendar Year (Jan–Dec)</option>
                <option value="financial">Financial Year (Apr–Mar)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {yearType === 'financial' ? 'FY (starting)' : 'Year'}
              </label>
              <input type="number" min="2000" max="2100" value={year}
                     onChange={e => setYear(Number(e.target.value))}
                     className="border rounded px-3 py-2 text-sm w-32" data-testid="report-year" />
              {yearType === 'financial' && (
                <p className="text-[10px] text-gray-500 mt-0.5">→ {year}-Apr to {year + 1}-Mar</p>
              )}
            </div>
          </>
        )}

        <button onClick={runPreview} disabled={!canRun || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="run-report-btn">
          {loading && <Loader size={14} className="animate-spin" />} Run
        </button>

        {preview && (
          <div className="ml-auto flex gap-2">
            <button onClick={() => download('xlsx')} disabled={downloading}
                    className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 inline-flex items-center gap-2"
                    data-testid="download-xlsx">
              {downloading === 'xlsx' ? <Loader size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
              Excel
            </button>
            <button onClick={() => download('pdf')} disabled={downloading}
                    className="px-3 py-2 border border-red-600 text-red-700 rounded-lg hover:bg-red-50 inline-flex items-center gap-2"
                    data-testid="download-pdf">
              {downloading === 'pdf' ? <Loader size={14} className="animate-spin" /> : <FileText size={14} />}
              PDF
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 text-sm rounded px-3 py-2">{error}</div>
      )}

      {preview && (
        <>
          <div className="mb-3">
            <h2 className="text-xl font-semibold text-gray-900" data-testid="report-title">{preview.title}</h2>
            <p className="text-sm text-gray-600">{preview.subtitle}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <SummaryTile label="Total" value={preview.summary.total} color="bg-gray-50" />
            <SummaryTile label="Complied" value={preview.summary.complied} color="bg-emerald-50 text-emerald-800" />
            <SummaryTile label="Not Complied" value={preview.summary.not_complied} color="bg-red-50 text-red-800" />
            <SummaryTile label="On Track" value={preview.summary.on_track} color="bg-blue-50 text-blue-800" />
            <SummaryTile label="Not Applicable" value={preview.summary.not_applicable || 0} color="bg-gray-100 text-gray-600" />
            <SummaryTile label="Unassigned" value={preview.summary.unassigned} color="bg-amber-50 text-amber-800" />
          </div>

          <div className="bg-white border rounded-lg overflow-x-auto" data-testid="report-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-700 whitespace-nowrap">Due Date</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700 whitespace-nowrap">Internal Due</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Fund</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Section</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Compliance</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Type</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Assignee</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">WIP</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Status</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Completed</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">No compliance occurrences match this report.</td></tr>
                ) : preview.rows.map(r => (
                  <tr key={r.id} className="border-t">
                    <td className="px-4 py-2 font-mono whitespace-nowrap">{fmtDate(r.due_date)}</td>
                    <td className="px-4 py-2 font-mono whitespace-nowrap text-gray-600">{r.internal_due_date ? fmtDate(r.internal_due_date) : '—'}</td>
                    <td className="px-4 py-2">{r.fund_name}</td>
                    <td className="px-4 py-2 text-gray-600">{r.section_name}</td>
                    <td className="px-4 py-2 font-medium text-gray-900">{r.item_name}</td>
                    <td className="px-4 py-2 capitalize">{(r.compliance_type || '').replace('_', '-')}</td>
                    <td className="px-4 py-2">
                      {r.assignee_name === 'Unassigned'
                        ? <span className="text-xs px-2 py-0.5 rounded border bg-red-100 text-red-800 border-red-300 font-medium">Unassigned</span>
                        : r.assignee_name}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{r.wip_status}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_BADGE[r.compliance_status] || ''}`}>{r.compliance_status}</span>
                    </td>
                    <td className="px-4 py-2 font-mono text-gray-600">{r.completion_date ? fmtDate(r.completion_date) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.row_count > preview.rows.length && (
              <div className="text-xs text-gray-500 italic px-4 py-2 border-t bg-gray-50">
                Showing first {preview.rows.length} of {preview.row_count} rows in preview. Download for the full list.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, color }) => (
  <div className={`border rounded-lg px-4 py-3 ${color}`}>
    <div className="text-xs uppercase tracking-wide opacity-75">{label}</div>
    <div className="text-2xl font-semibold mt-0.5">{value}</div>
  </div>
);

export default ComplianceReports;
